import { PayloadAction } from "@reduxjs/toolkit";
import { AnyAction } from "redux";

import {
  IDataKeys,
  IJSONAPIItem,
  FulfilledAction,
  IActionStates,
  PendingAction,
  RejectedAction,
} from "shared/types";
import { VENUE_ACTIONS, VENUE_ACTION_PREFIX } from "Venues/types";

const matchPrefix = (action: AnyAction): boolean => action.type.startsWith(VENUE_ACTION_PREFIX);

// These actions are not be handled by the matchers for the fulfilled state
const isFulfilledActionExceptionType = (action: AnyAction): boolean =>
  isDeleteFulfilledActionType(action) ||
  isCreatedFulfilledActionType(action) ||
  isReplaceFulfilledActionType(action) ||
  isVenueDetailFulfilledActionType(action) ||
  isEditFulfilledActionType(action);

// These actions are not handled by the general matcher for the fulfilled state
// but by the created fulfilled matcher that passes the id of the created item to the meta
const isCreatedFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(VENUE_ACTIONS.createStage) ||
  action.type.startsWith(VENUE_ACTIONS.createVenue) ||
  action.type.startsWith(VENUE_ACTIONS.createStageCapacity) ||
  action.type.startsWith(VENUE_ACTIONS.createEquipment) ||
  action.type.startsWith(VENUE_ACTIONS.createInfoBlockItem);

// These actions are not handled by the general matcher for the fulfilled state
// but by the deleted fulfilled matcher that removes the item from the state
const isDeleteFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(VENUE_ACTIONS.deleteStage) ||
  action.type.startsWith(VENUE_ACTIONS.deleteVenue) ||
  action.type.startsWith(VENUE_ACTIONS.deleteInfoBlock) ||
  action.type.startsWith(VENUE_ACTIONS.deleteInfoBlockItem);

// These actions are not handled by the general matcher for the fulfilled state
// but by the edit fulfilled matcher that replaces the item in the state
const isEditFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(VENUE_ACTIONS.editStage) ||
  action.type.startsWith(VENUE_ACTIONS.editEquipment) ||
  action.type.startsWith(VENUE_ACTIONS.editStageEquipmentCategories) ||
  action.type.startsWith(VENUE_ACTIONS.editInfoBlock) ||
  action.type.startsWith(VENUE_ACTIONS.editVenueBands) ||
  action.type.startsWith(VENUE_ACTIONS.editInfoBlockItem);

// These actions are not handled by the general matcher for the fulfilled state
// but by the replace fulfilled matcher that replaces the data in the state instead of merging it
const isReplaceFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(VENUE_ACTIONS.getUserVenues) ||
  action.type.startsWith(VENUE_ACTIONS.getSearchVenues);

// These actions are not handled by the general matcher for the fulfilled state
// but by the venue detail fulfilled matcher that merges the venue state with the getMergedVenueState method
const isVenueDetailFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(VENUE_ACTIONS.getVenue) ||
  action.type.startsWith(VENUE_ACTIONS.editVenue) ||
  action.type.startsWith(VENUE_ACTIONS.editVenueStageOrder);

// These actions are handled by the general matcher for the fulfilled state
// but also by the info block detail fulfilled matcher that adds its linked
// contacts, files, places and images to the redux store under the right key
const isInfoBlockDetailFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(VENUE_ACTIONS.getInfoBlock) ||
  action.type.startsWith(VENUE_ACTIONS.editInfoBlock);

const isPaginatedFulfilledActionType = (action: AnyAction): boolean =>
  action.type.startsWith(VENUE_ACTIONS.getEquipment) ||
  action.type.startsWith(VENUE_ACTIONS.getStageEquipment) ||
  action.type.startsWith(VENUE_ACTIONS.getVenueInfoBlockItems) ||
  action.type.startsWith(VENUE_ACTIONS.getUserVenues) ||
  action.type.startsWith(VENUE_ACTIONS.getSearchVenues);

export const isPendingAction = (action: AnyAction): action is PendingAction => {
  return matchPrefix(action) && action.type.endsWith(`/${IActionStates.pending}`);
};
export const isRejectedAction = (action: AnyAction): action is RejectedAction => {
  return matchPrefix(action) && action.type.endsWith(`/${IActionStates.rejected}`);
};
export const isFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isFulfilledActionExceptionType(action)) return false;
  return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
};

// For create actions where you want to pass the created items id and/or item to the meta
export const isCreatedFulfilledAction = (
  action: AnyAction
): action is PayloadAction<{ data: any; id: string; createdItem?: IJSONAPIItem }, string, any> => {
  if (isCreatedFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For delete actions where you want to remove the item from the state
export const isDeletedFulfilledAction = (
  action: AnyAction
): action is PayloadAction<{ path: string; dataKey: IDataKeys }, string, any> => {
  if (isDeleteFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For edit actions where you want to replace the item in the state
export const isEditFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isEditFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For actions where you want to replace all the items under the key in the state instead of merging
export const isReplaceFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isReplaceFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For actions where you want to replace all the items under the key in the state instead of merging
export const isVenueDetailFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isVenueDetailFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For actions where you want to add the linked contacts, files, places and images to the redux store
export const isInfoBlockDetailFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isInfoBlockDetailFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};

// For actions where you want to replace all the items under the key in the state instead of merging
export const isPaginatedFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  if (isPaginatedFulfilledActionType(action)) {
    return matchPrefix(action) && action.type.endsWith(`/${IActionStates.fulfilled}`);
  }
  return false;
};
